import { useLayoutEffect, useRef, useState } from 'react';
import { useIsSsr } from '@direct-frontend/stdlib';

const isServerEnvironment = typeof window === 'undefined';

// Во время серверного рендеринга подписка на изменение стора не создается,
// поэтому можно безопасно вызвать изменение стора во время рендеринга компонента
const useIsomorphicLayoutEffect = isServerEnvironment ? (effect: () => void) => effect() : useLayoutEffect;

/**
 * Записывает начальное состояние формы до первой вставки компонента в DOM.
 *
 * Экшен выполняется одним из двух способов:
 * - прямо в рендере – на сервере всегда, на клиенте только на проходе гидрации и только если
 *   разрешил `canRunDuringRender`. Тогда результат виден селекторам уже на этом проходе,
 *   и клиентская разметка совпадает с серверной;
 * - в `useLayoutEffect` – во всех остальных случаях. Первый рендер тогда проходит с результатом
 *   `false`, после эффекта хук принудительно перерендеривает компонент, чтобы селекторы
 *   увидели актуальные значения.
 *
 * React вправе выбросить и повторить рендер (в том числе в StrictMode), поэтому и проверка,
 * и сам экшен на пути рендера обязаны быть идемпотентными.
 *
 * @param initAction Вызываемый экшен. Экшен будет вызван повторно при изменении ссылки на функцию
 * @param canRunDuringRender Разрешает вызвать экшен в рендере. Спрашивается только на проходе
 * гидрации и только на первом рендере компонента; если вернула `false`, экшен уходит
 * в `useLayoutEffect` и повторно проверка не вызывается
 * @returns `true`, если дочерние компоненты можно рендерить. Вне гидрации это означает, что экшен
 * уже выполнен – в рендере или в layout-эффекте; на проходе гидрации `true` возвращается сразу,
 * независимо от того, выполнился ли экшен: серверная разметка уже отрисована, и прятать за `false`
 * нечего. Кому нужна именно инициализированность данных – проверяет ее отдельно
 */
export const useSyncFormInitialization = (initAction: () => void, canRunDuringRender: () => boolean) => {
    const isSsr = useIsSsr();
    const [inited, setInited] = useState(isSsr);

    // Экшен, который уже отработал в рендере. Держим в ref, а не в состоянии, чтобы не
    // удерживать замыкание после того, как layout-эффект отработал.
    const calledDuringRenderRef = useRef<(() => void) | null>(null);

    // Ленивый инициализатор useState выполняется на первом рендере компонента – там же,
    // где экшен выполняется на сервере. Поэтому результат виден селекторам уже на этом проходе,
    // и клиентская разметка совпадает с серверной.
    // На клиенте это допустимо только на проходе гидрации: вне его серверной разметки нет,
    // чинить нечего, а отброшенный React-ом рендер оставил бы запись в сторе.
    // eslint-disable-next-line react-hooks/refs -- ref только пишется в рендере, читают его в layout-эффекте
    const [isCalledDuringRender] = useState(() => {
        if (isServerEnvironment || !isSsr || !canRunDuringRender()) {
            return false;
        }

        initAction();
        calledDuringRenderRef.current = initAction;

        return true;
    });

    useIsomorphicLayoutEffect(() => {
        // Этот экшен уже отработал в рендере – повторно вызывать нечего
        if (calledDuringRenderRef.current !== initAction) {
            initAction();
        }

        // Дальше сравнивать не с чем – отпускаем замыкание экшена
        calledDuringRenderRef.current = null;

        // Первый вызов useLayoutEffect происходит еще до появления подписок на сторы,
        // поэтому триггерим повторный рендеринг вручную, чтобы селекторы в компоненте подхватили актуальные значения.
        // После вызова в рендере селекторы и так видят актуальные значения, ререндер не нужен.
        if (!isServerEnvironment && !inited && !isCalledDuringRender) {
            setInited(true);
        }
    }, [initAction]);

    return inited || isCalledDuringRender;
};
